import React from 'react';
import styled, { keyframes } from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CanvasFrame = styled.iframe`
  flex: 1;
  width: 100%;
  border: none;
  background: var(--bg-primary, #0f172a);
`;

const EmptyCanvas = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const ProcessingCanvas = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const ProcessingDots = styled.div`
  display: flex;
  gap: 6px;
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent, #3b82f6);
`;

const ProcessingLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

interface AnalyzeCanvasProps {
  html: string | null;
  url: string | null;
  isProcessing: boolean;
  hasMessages: boolean;
}

const AnalyzeCanvas: React.FC<AnalyzeCanvasProps> = ({ html, url, isProcessing, hasMessages }) => {
  if (!html && !url) {
    if (isProcessing) {
      return (
        <ProcessingCanvas>
          <ProcessingDots>
            <Dot />
            <Dot />
            <Dot />
          </ProcessingDots>
          <ProcessingLabel>Tracking activities and building analysis...</ProcessingLabel>
        </ProcessingCanvas>
      );
    }

    if (hasMessages) {
      return (
        <EmptyCanvas>
          <div style={{ fontSize: '2rem' }}>&#x1F4CA;</div>
          <div>Analysis will build up as the plan executes</div>
        </EmptyCanvas>
      );
    }

    return (
      <EmptyCanvas>
        <div style={{ fontSize: '2rem' }}>&#x1F4CA;</div>
        <div>Analysis results will appear here</div>
      </EmptyCanvas>
    );
  }

  return (
    <Wrapper>
      {url ? (
        <CanvasFrame src={url} sandbox="allow-scripts" title="Analyze canvas" />
      ) : (
        <CanvasFrame srcDoc={html!} sandbox="allow-scripts" title="Analyze canvas" />
      )}
    </Wrapper>
  );
};

export default AnalyzeCanvas;
